import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import './AdminPages.css';

const HomepageComponentsManager = () => {
  const navigate = useNavigate();
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [formData, setFormData] = useState({
    type: 'slider',
    title: '',
    content: '',
    order_index: 0,
    is_enabled: true,
    is_published: false
  });

  // Sample templates for different component types
  const componentTemplates = {
    slider: `[
  {
    "image": "/images/slider1.jpg",
    "title": "Welcome to Our Store",
    "subtitle": "Quality products at great prices",
    "cta": "Shop Now",
    "link": "/products"
  },
  {
    "image": "/images/slider2.jpg",
    "title": "New Arrivals",
    "subtitle": "Check out our latest products",
    "cta": "Explore",
    "link": "/products"
  }
]`,
    testimonials: `[
  {
    "name": "John Doe",
    "role": "Business Owner",
    "image": "/images/testimonial1.jpg",
    "rating": 5,
    "text": "Excellent service and great products!"
  },
  {
    "name": "Jane Smith",
    "role": "Marketing Manager",
    "image": "/images/testimonial2.jpg",
    "rating": 5,
    "text": "Fast shipping and quality products."
  }
]`,
    features: `[
  {
    "icon": "🚚",
    "title": "Free Shipping",
    "description": "On orders over $50"
  },
  {
    "icon": "💳",
    "title": "Secure Payment",
    "description": "100% secure transactions"
  },
  {
    "icon": "↩️",
    "title": "Easy Returns",
    "description": "30-day return policy"
  }
]`,
    cta: `{
  "title": "Ready to Get Started?",
  "description": "Join thousands of satisfied customers today",
  "button_text": "Shop Now",
  "button_link": "/products"
}`,
    stats: `[
  {
    "number": "10,000+",
    "label": "Happy Customers"
  },
  {
    "number": "500+",
    "label": "Products"
  },
  {
    "number": "50+",
    "label": "Countries"
  },
  {
    "number": "99%",
    "label": "Satisfaction Rate"
  }
]`
  };

  useEffect(() => {
    fetchComponents();
  }, []);

  const fetchComponents = async () => {
    try {
      const response = await api.get('/admin/homepage-components');
      setComponents(response.data || []);
    } catch (err) {
      setError('Failed to load homepage components');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setFormData(prev => ({
      ...prev,
      type: newType,
      content: componentTemplates[newType] || ''
    }));
  };

  const resetForm = () => {
    setFormData({
      type: 'slider',
      title: '',
      content: '',
      order_index: 0,
      is_enabled: true,
      is_published: false
    });
    setShowForm(false);
    setEditingComponent(null);
  };

  const validateJSON = (jsonString) => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate JSON content
    if (formData.content && !validateJSON(formData.content)) {
      setError('Invalid JSON format in content field');
      setTimeout(() => setError(''), 5000);
      return;
    }

    const componentData = {
      ...formData,
      order_index: parseInt(formData.order_index)
    };

    try {
      if (editingComponent) {
        await api.put(`/admin/homepage-components/${editingComponent.id}`, componentData);
        setSuccess('Component updated successfully!');
      } else {
        await api.post('/admin/homepage-components', componentData);
        setSuccess('Component created successfully!');
      }
      resetForm();
      fetchComponents();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save component');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleEdit = (component) => {
    setEditingComponent(component);
    setFormData({
      type: component.type,
      title: component.title || '',
      content: component.content ? JSON.stringify(JSON.parse(component.content), null, 2) : '',
      order_index: component.order_index,
      is_enabled: component.is_enabled,
      is_published: component.is_published
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this component?')) return;

    try {
      await api.delete(`/admin/homepage-components/${id}`);
      setSuccess('Component deleted successfully!');
      fetchComponents();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete component');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handlePublish = async (id) => {
    try {
      await api.post(`/admin/homepage-components/${id}/publish`);
      setSuccess('Component published successfully!');
      fetchComponents();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to publish component');
      setTimeout(() => setError(''), 5000);
    }
  };

  if (loading) return <div className="loading">Loading components...</div>;

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <h1>Homepage Components Manager</h1>
          <div className="button-group">
            <button onClick={() => navigate('/admin')} className="btn btn-secondary">
              Back to Dashboard
            </button>
            <button onClick={() => setShowForm(true)} className="btn btn-primary">
              + New Component
            </button>
          </div>
        </div>

        {success && <div className="success">{success}</div>}
        {error && <div className="error">{error}</div>}

        {/* Component Form */}
        {showForm && (
          <div className="form-modal">
            <div className="form-modal-content form-modal-wide">
              <div className="form-modal-header">
                <h2>{editingComponent ? 'Edit Component' : 'New Component'}</h2>
                <button className="close-btn" onClick={resetForm}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="type">Component Type *</label>
                    <select
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleTypeChange}
                      required
                    >
                      <option value="slider">Slider / Banner</option>
                      <option value="testimonials">Testimonials</option>
                      <option value="features">Features Grid</option>
                      <option value="cta">Call to Action</option>
                      <option value="stats">Statistics</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="title">Title</label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Optional title for the component"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="order_index">Display Order</label>
                    <input
                      type="number"
                      id="order_index"
                      name="order_index"
                      value={formData.order_index}
                      onChange={handleInputChange}
                      min="0"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="content">Content (JSON) *</label>
                  <textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    rows="15"
                    placeholder="Enter JSON content for the component"
                    required
                    style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                  />
                  <small style={{ color: '#718096', marginTop: '0.5rem', display: 'block' }}>
                    Use the template loaded based on component type. Modify as needed.
                  </small>
                </div>

                <div className="form-group-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      name="is_enabled"
                      checked={formData.is_enabled}
                      onChange={handleInputChange}
                    />
                    <span>Enable component</span>
                  </label>
                </div>

                <div className="form-group-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      name="is_published"
                      checked={formData.is_published}
                      onChange={handleInputChange}
                    />
                    <span>Publish immediately</span>
                  </label>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={resetForm} className="btn btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingComponent ? 'Update Component' : 'Create Component'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Components List */}
        <div className="data-table">
          {components.length === 0 ? (
            <div className="empty-state">
              <p>No homepage components yet. Create your first component!</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Enabled</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {components.map(component => (
                  <tr key={component.id}>
                    <td>
                      <strong>{component.order_index}</strong>
                    </td>
                    <td>
                      <span className="type-badge">{component.type}</span>
                    </td>
                    <td>{component.title || '-'}</td>
                    <td>
                      <span className={`status-badge ${component.is_published ? 'published' : 'draft'}`}>
                        {component.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${component.is_enabled ? 'enabled' : 'disabled'}`}>
                        {component.is_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(component)}
                          className="btn-icon btn-edit"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        {!component.is_published && (
                          <button
                            onClick={() => handlePublish(component.id)}
                            className="btn-icon btn-publish"
                            title="Publish"
                          >
                            📤
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(component.id)}
                          className="btn-icon btn-delete"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomepageComponentsManager;
